import './PredictionInspector.scss';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faArrowLeft, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { Button } from '@mui/joy';
import classNames from 'classnames';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Loader } from '../../../../__legacy__/components/Loader';
import { HiddenAnnotationsToastContainer } from '../../../../__legacy__/containers/HiddenAnnotationsToast.container';
import { useLoader } from '../../../../__legacy__/helpers/useLoader';
import { IAnnotationService } from '../../../../__legacy__/modules/annotation/annotation.service';
import { IAnnotationsStore } from '../../../../__legacy__/modules/annotation/annotations.store';
import { IAnnotationUiStore } from '../../../../__legacy__/modules/annotation/annotationUi.store';
import { AnnotationTopBarItem } from '../../../../__legacy__/modules/annotation/components/AnnotationTopBarItem';
import { AnnotationViewOptions } from '../../../../__legacy__/modules/annotation/components/AnnotationViewOptions';
import { Image } from '../../../../__legacy__/modules/annotation/components/Image';
import { AnnotationImageNameHeaderContainer } from '../../../../__legacy__/modules/annotation/containers/AnnotationImageNameHeader.container';
import { DrawingContainer } from '../../../../__legacy__/modules/annotation/containers/Drawing.container';
import { ImageContainer } from '../../../../__legacy__/modules/annotation/containers/Image.container';
import { SegmentationsContainer } from '../../../../__legacy__/modules/annotation/containers/Segmentations.container';
import { ViewAttributesContainer } from '../../../../__legacy__/modules/annotation/containers/ViewAttributesContainer';
import { IAnnotationTypeBl } from '../../../../__legacy__/modules/annotation/submodules/annotationTypes/annotationType.bl';
import { IAnnotationTypeStore } from '../../../../__legacy__/modules/annotation/submodules/annotationTypes/annotationType.store';
import { LeftMenuContainer } from '../../../../__legacy__/modules/annotation/submodules/segmentations/containers/LeftMenu.container';
import { ImageSegmentationsContextProvider } from '../../../../__legacy__/modules/annotation/submodules/segmentations/segmentationContextMenu/imageSegmentations.context';
import { Home } from '../../../../__legacy__/routes/config/Home';
import { useCustomHotkeys } from '../../../../common/hooks/useCustomHotkeys';
import { IOverlayLoaderStore } from '../../../common/OverlayLoader.store';
import { InspectorImageInfo } from '../../components/predictionInspector/InspectorImageInfo';
import { InspectorPrediction } from '../../components/predictionInspector/InspectorPrediction';
import { PredictedPoints } from '../../components/predictionInspector/PredictedPoints';
import { PredictedRectangles } from '../../components/predictionInspector/PredictedRectangles';
import { InspetorSettings } from '../../components/predictionInspector/PredictionImagesSettings';
import { IJobDetailsService } from '../../jobDetails.service';
import {
  INFERENCE_TYPE,
  IValidJobType,
  JOB_TYPE,
  TRAINING_VARIANT,
  TRAINING_VARIANTS_WITH_HEATMAP,
  TRAINING_VARIANTS_WITH_MASK,
  TRAINING_VARIANTS_WITHOUT_RESULTS,
  TRAINING_VARIANTS_WITHOUT_THRESHOLD,
} from '../../jobs.model';
import { IJobThresholdStore } from '../../jobThreshold.store';
import { IPredictionImagePreviewService } from '../../predictionInspector.service';
import { assertNotNull } from '../../utils';

library.add(faArrowLeft, faEye, faEyeSlash);
const UPDATE_LOADER_KEY = 'prediction-inspector-update-loader';

interface IPredictionInspectorContainer {
  jobType: IValidJobType;
}

export const PredictionInspectorContainer = observer((props: IPredictionInspectorContainer): ReactElement => {
  const predictionInspectorService = useInjection(IPredictionImagePreviewService.$);
  const jobDetailsService = useInjection(IJobDetailsService.$);
  const annotationTypeStore = useInjection(IAnnotationTypeStore.$);
  const annotationTypeBl = useInjection(IAnnotationTypeBl.$);
  const annotationService = useInjection(IAnnotationService.$);
  const annotationsStore = useInjection(IAnnotationsStore.$);
  const annotationUiStore = useInjection(IAnnotationUiStore.$);
  const thresholdStore = useInjection(IJobThresholdStore.$);
  const loaderStore = useInjection(IOverlayLoaderStore.$);

  const { workspaceId, jobId, imageId } = useParams<{ workspaceId: string; jobId: string; imageId: string }>();
  const history = useHistory();
  const { t } = useTranslator();

  const [showHeatmap, setShowHeatmap] = useState(true);
  const { isLoading: isInitializing, withLoaderAsync: withInitializing } = useLoader(true);
  const updatePending = loaderStore.isSpinnerVisible(UPDATE_LOADER_KEY);
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [showMask, setShowMask] = useState(false);
  const [predictionClassIds, setPredictionClassIds] = useState<string[]>([]);
  const {
    job: { variant, type },
  } = jobDetailsService.store;
  const { predictionInspectorStore } = predictionInspectorService;
  const hasMask = variant !== null && TRAINING_VARIANTS_WITH_MASK.includes(variant);
  const hasThreshold = variant !== null && !TRAINING_VARIANTS_WITHOUT_THRESHOLD.includes(variant);
  const hasHeatmap = variant !== null && TRAINING_VARIANTS_WITH_HEATMAP.includes(variant);
  const hasResults = variant !== null && !TRAINING_VARIANTS_WITHOUT_RESULTS.includes(variant);
  const isThresholdReadonly = variant === null || ![TRAINING_VARIANT.LOCATE_POINTS, TRAINING_VARIANT.LOCATE_OBJECTS].includes(variant);
  const thresholdConfig = thresholdStore.threshold;

  useEffect(() => {
    return (): void => {
      predictionInspectorService.dispose();
    };
  }, []);

  const initialize = async (): Promise<void> =>
    withInitializing(async () => {
      await predictionInspectorService.initializeAsync(workspaceId, jobId, props.jobType);
      await predictionInspectorService.updateImageId(imageId);
      const variant = jobDetailsService.store.job.variant;
      const predictionDetails = predictionInspectorStore.predictionDetails;
      assertNotNull(variant, 'Tried to show predictions without job info');
      assertNotNull(predictionDetails, 'Tried to show predictions without image selected');
      if (![TRAINING_VARIANT.LOCATE_OBJECTS, TRAINING_VARIANT.LOCATE_POINTS].includes(variant)) {
        return;
      }

      const { predictedRectangles, predictedPoints } = predictionDetails;
      if (variant === TRAINING_VARIANT.LOCATE_POINTS && predictedPoints) {
        setPredictionClassIds(predictedPoints.map((predictedPoint) => predictedPoint.classId));
      }

      if (variant === TRAINING_VARIANT.LOCATE_OBJECTS && predictedRectangles) {
        setPredictionClassIds(predictedRectangles.map((predictedRectangle) => predictedRectangle.classId));
      }
    });

  const updateImageId = async (): Promise<void> => {
    loaderStore.enableLoader(UPDATE_LOADER_KEY);
    await predictionInspectorService.updateImageId(imageId);
    loaderStore.disableLoader(UPDATE_LOADER_KEY);
  };

  useEffect(() => {
    void initialize();
  }, []);

  useEffect(() => {
    if (isInitializing) {
      return;
    }
    void updateImageId();
  }, [imageId, isInitializing]);

  useEffect(() => {
    if (annotationTypeStore.annotationTypesOptions.some((annotationTypeOption) => annotationTypeOption.isVisible)) {
      setShowAnnotations(true);
    } else {
      setShowAnnotations(false);
    }
  }, [annotationTypeStore.annotationTypesOptions]);

  const handleThresholdChange = (value: number): void => {
    if (isNaN(value) || !thresholdConfig) {
      return;
    }

    if (thresholdStore.threshold?.optimalThreshold !== value) {
      thresholdStore.threshold = {
        ...thresholdConfig,
        optimalThreshold: value,
      };
    }
  };

  const navigate = (forward: boolean): void => {
    const parameters = {
      workspaceId,
      jobId,
      imageId: forward ? predictionInspectorStore.nextImage : predictionInspectorStore.previousImage,
    };

    const jobType = props.jobType;

    switch (jobType) {
      case JOB_TYPE.INFERENCE:
        history.push(Home.Inferences.Details.PredictionInspector.withParams(parameters));
        break;
      case JOB_TYPE.TRAINING:
        history.push(Home.Trainings.Details.PredictionInspector.withParams(parameters));
        break;
    }
  };

  const backLink = (): string => {
    const jobType = props.jobType;
    const parameters = {
      workspaceId,
      jobId,
    };
    switch (jobType) {
      case JOB_TYPE.INFERENCE:
        return Home.Inferences.Details.Images.withParams(parameters);
      case JOB_TYPE.TRAINING:
        return Home.Trainings.Details.Images.withParams(parameters);
      default:
        return '';
    }
  };

  const handleToggleShowHeatmap = (): void => {
    setShowHeatmap((currentValue) => {
      const newValue = !currentValue;
      if (newValue) {
        setShowMask(false);
      }

      return newValue;
    });
  };
  const handleToggleShowMask = (): void => {
    setShowMask((currentValue) => {
      const newValue = !currentValue;
      if (newValue) {
        setShowHeatmap(false);
      }

      return newValue;
    });
  };

  const handleToggleShowAnnotations = (): void => {
    const annotationTypesOptions = annotationTypeStore.annotationTypesOptions;

    annotationTypesOptions.forEach((x) => {
      const featureIdsToShow = annotationsStore.segmentations
        .filter((s) => x.id === s.feature.properties?.annotationTypeId)
        .map((s) => ({ segmentationId: s.id, featureId: s.feature.id }));
      featureIdsToShow.forEach((s) => {
        annotationService.showSegmentation(s.featureId, s.segmentationId);
      });

      annotationTypeBl.setAnnotationTypeOptions(predictionInspectorService.annotationsStore.projectId, { ...x, isVisible: !showAnnotations });
    });
  };

  const handleTogglePredictionClassId = (classId: string) => (): void => {
    setPredictionClassIds((state) => (state.includes(classId) ? state.filter((point) => point !== classId) : [...state, classId]));
  };

  const handleLeft = (): void => {
    if (predictionInspectorStore.indexOf > 0) {
      loaderStore.enableLoader(UPDATE_LOADER_KEY);
      navigate(false);
    }
  };

  const handleRight = (): void => {
    if (predictionInspectorStore.indexOf + 1 < predictionInspectorStore.total) {
      loaderStore.enableLoader(UPDATE_LOADER_KEY);
      navigate(true);
    }
  };

  useCustomHotkeys('left', handleLeft, { keydown: false, keyup: true, enabled: !updatePending }, [handleLeft, updatePending]);
  useCustomHotkeys('right', handleRight, { keydown: false, keyup: true, enabled: !updatePending }, [handleRight, updatePending]);

  if (isInitializing) {
    return <Loader isLoading={true} />;
  }

  return (
    <div className='annotation-view prediction-inspector'>
      <ImageSegmentationsContextProvider imageId={imageId}>
        <div className='annotation-view-top-bar'>
          <AnnotationTopBarItem>
            <Button
              color='neutral'
              variant='topbar'
              onClick={() => {
                history.push(backLink());
              }}
              startDecorator={<FontAwesomeIcon icon={faArrowLeft} />}
            >
              {t('back')}
            </Button>
          </AnnotationTopBarItem>
          <AnnotationTopBarItem>
            <AnnotationViewOptions disableCursorConfig={true} />
          </AnnotationTopBarItem>
          <AnnotationTopBarItem>
            <Button
              color='neutral'
              variant='topbar'
              onClick={handleToggleShowAnnotations}
              disabled={type === INFERENCE_TYPE.DATASET}
              startDecorator={<FontAwesomeIcon icon={showAnnotations ? faEye : faEyeSlash} />}
            >
              {t('annotations')}
            </Button>
          </AnnotationTopBarItem>
          <AnnotationTopBarItem>
            <ViewAttributesContainer />
          </AnnotationTopBarItem>
          {hasHeatmap && (
            <AnnotationTopBarItem>
              <Button
                color='neutral'
                variant='topbar'
                onClick={handleToggleShowHeatmap}
                startDecorator={<FontAwesomeIcon icon={showHeatmap ? faEye : faEyeSlash} />}
              >
                {t('heatmap')}
              </Button>
            </AnnotationTopBarItem>
          )}
          {hasMask && (
            <AnnotationTopBarItem>
              <Button
                color='neutral'
                variant='topbar'
                onClick={handleToggleShowMask}
                startDecorator={<FontAwesomeIcon icon={showMask ? faEye : faEyeSlash} />}
              >
                {t('output')}
              </Button>
            </AnnotationTopBarItem>
          )}
          <AnnotationTopBarItem variation='image-name'>
            <AnnotationImageNameHeaderContainer />
          </AnnotationTopBarItem>
          <AnnotationTopBarItem variation='image-navigation-item'>
            <div className='image-navigation'>
              <Button
                color='neutral'
                variant='transparent'
                startDecorator={<FontAwesomeIcon icon='angle-left' />}
                onClick={handleLeft}
                disabled={predictionInspectorStore.indexOf < 1 || updatePending}
              >
                <span className='description'>{t('previous')}</span>
              </Button>
              <span className='images-count'>
                {predictionInspectorStore.indexOf + 1} {t('of')} {predictionInspectorStore.total}
              </span>
              <Button
                color='neutral'
                variant='transparent'
                endDecorator={<FontAwesomeIcon icon='angle-right' />}
                onClick={handleRight}
                disabled={predictionInspectorStore.indexOf + 1 >= predictionInspectorStore.total || updatePending}
              >
                <span className='description'>{t('next')}</span>
              </Button>
            </div>
          </AnnotationTopBarItem>
        </div>
        <div className='annotation-view-inner'>
          <LeftMenuContainer
            isReadOnly={true}
            showObjectsList={true}
            isReview={false}
          />
          <HiddenAnnotationsToastContainer />
          <ImageContainer
            maskUrl={showMask ? predictionInspectorStore.predictionDetails?.maskUrl : null}
            heatmapUrl={showHeatmap ? predictionInspectorStore.predictionDetails?.heatmapUrl : null}
          >
            {(imageProps): ReactElement => (
              <Image {...imageProps}>
                <SegmentationsContainer readonly={true} />
                <DrawingContainer disableCursorGuides={false} />
                {variant === TRAINING_VARIANT.LOCATE_POINTS && (
                  <PredictedPoints
                    predictedPoints={predictionInspectorStore.predictionDetails?.predictedPoints}
                    threshold={thresholdConfig?.optimalThreshold ?? 0}
                    predictionClassIds={predictionClassIds}
                  />
                )}
                {variant === TRAINING_VARIANT.LOCATE_OBJECTS && (
                  <PredictedRectangles
                    predictedRectangles={predictionInspectorStore.predictionDetails?.predictedRectangles}
                    threshold={thresholdConfig?.optimalThreshold ?? 0}
                    predictionClassIds={predictionClassIds}
                    zoomLevel={annotationUiStore.zoomLevel}
                  />
                )}
              </Image>
            )}
          </ImageContainer>
          <div className='right-column'>
            <div className='scrollable-container'>
              <div className='scrollable-container-content'>
                <InspectorImageInfo image={predictionInspectorStore.image} />
                {hasThreshold && (
                  <InspetorSettings
                    thresholdConfig={thresholdConfig}
                    handleChangeThreshold={handleThresholdChange}
                    isReadonly={isThresholdReadonly}
                    variant={variant}
                  />
                )}

                {variant !== null && hasResults && (
                  <div className={classNames('predictions-view', {})}>
                    <div className='header'>
                      <FontAwesomeIcon icon='chart-bar' /> {t('results')}
                    </div>
                    {annotationUiStore.isImageLoading || updatePending ? (
                      <Loader isLoading />
                    ) : (
                      <div className='predictions-view-inner'>
                        {predictionInspectorStore.predictionDetails ? (
                          <InspectorPrediction
                            thresholdConfig={thresholdConfig}
                            data={predictionInspectorStore.predictionDetails}
                            variant={variant}
                            handleTogglePredictionClassId={handleTogglePredictionClassId}
                            predictionClassIds={predictionClassIds}
                          />
                        ) : (
                          t('no_results_for_current_image')
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </ImageSegmentationsContextProvider>
    </div>
  );
});
